/**
 * Layout Component - Modern Persistent Layout
 * Always-visible sidebar with responsive content area
 */

import React, { useState, useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Breadcrumbs from './Breadcrumbs';
import Footer from './Footer';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { KeyboardShortcutHelperRef } from './KeyboardShortcutHelper';

interface LayoutProps {
  children: React.ReactNode;
}

const DRAWER_WIDTH = 260;

const MainContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F9FAFB',
});

const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sidebarOpen',
})<{ sidebarOpen: boolean }>(({ sidebarOpen }) => ({
  flexGrow: 1,
  minWidth: 0,        // prevents flex child from expanding beyond parent
  overflow: 'hidden', // clip content wider than available space
  marginLeft: sidebarOpen ? DRAWER_WIDTH : 0,
  transition: 'margin-left 225ms cubic-bezier(0.4, 0, 0.6, 1)',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

const ContentArea = styled(Box)({
  flexGrow: 1,
  padding: '24px 32px',
  marginTop: '64px',
}) as typeof Box;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const shortcutHelperRef = useRef<KeyboardShortcutHelperRef>(null);

  // Enable keyboard shortcuts globally
  useKeyboardShortcuts({
    onSaveDraft: () => {
      console.log('Save draft shortcut pressed');
      // TODO: Implement save draft functionality
    },
    onExport: () => {
      console.log('Export shortcut pressed');
      // TODO: Implement export functionality
    },
    onRefresh: () => {
      console.log('Refresh shortcut pressed');
      window.location.reload();
    },
    onShowShortcuts: () => {
      shortcutHelperRef.current?.open();
    },
  });

  const handleDrawerToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <MainContainer>
      {/* Skip to main content — visible only on keyboard focus */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          top: -40,
          left: 8,
          zIndex: 9999,
          px: 2,
          py: 1,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 1,
          fontSize: '14px',
          fontWeight: 600,
          textDecoration: 'none',
          transition: 'top 0.2s',
          '&:focus': { top: 8 },
        }}
      >
        Skip to main content
      </Box>

      {/* Persistent Sidebar */}
      <Sidebar open={sidebarOpen} onClose={handleDrawerToggle} variant={isDesktop ? 'persistent' : 'temporary'} />

      {/* Main Content with Navbar */}
      <ContentWrapper sidebarOpen={sidebarOpen && isDesktop}>
        {/* Top Navbar */}
        <Navbar 
          onMenuClick={handleDrawerToggle} 
          shortcutHelperRef={shortcutHelperRef}

        />

        {/* Page Content */}
        <ContentArea id="main-content" component="main" aria-label="Page content">
          {/* Breadcrumbs */}
          <Breadcrumbs />

          {/* Page Content */}
          <Box>{children}</Box>
        </ContentArea>

        {/* Footer */}
        <Footer />
      </ContentWrapper>


    </MainContainer>
  );
};

export default Layout;
